import { PartialMessage } from '@bufbuild/protobuf';
import { TxContext } from '@evmos/transactions';
import { RollappMetadata, DenomMetadata } from '../ts-client/metadata';
import { RollappMetadata as RollappMetadataProto } from '../proto/metadata_pb.js';
import { GenesisInfo as GenesisInfoProto } from '../proto/genesis_info_pb';
import { GenesisInfo } from '../ts-client/genesis_info';

export const METADATA_TYPES = (metadata?: RollappMetadata) => metadata && {
  TypeMetadata: [
    ...(metadata.website ? [ { name: 'website', type: 'string' } ] : []),
    ...(metadata.description ? [ { name: 'description', type: 'string' } ] : []),
    ...(metadata.logoUrl ? [ { name: 'logo_url', type: 'string' } ] : []),
    ...(metadata.telegram ? [ { name: 'telegram', type: 'string' } ] : []),
    ...(metadata.x ? [ { name: 'x', type: 'string' } ] : []),
    ...(metadata.genesisUrl ? [ { name: 'genesis_url', type: 'string' } ] : []),
    ...(metadata.displayName ? [ { name: 'display_name', type: 'string' } ] : []),
    ...(metadata.tagline ? [ { name: 'tagline', type: 'string' } ] : []),
    ...(metadata.explorerUrl ? [ { name: 'explorer_url', type: 'string' } ] : []),
    ...(metadata.feeDenom ? [ { name: 'fee_denom', type: 'TypeMetadataFeeDenom' } ] : []),
    ...(metadata.tags ? [ { name: 'tags', type: 'string[]' } ] : []),
  ],
  ...(metadata.feeDenom && {
    TypeMetadataFeeDenom: [
      { name: 'display', type: 'string' },
      { name: 'base', type: 'string' },
      { name: 'exponent', type: 'uint32' },
    ],
  }),
};

export const GENESIS_INFO_TYPES = (genesisInfo?: GenesisInfo) => genesisInfo && {
  TypeGenesisInfo: [
    ...(genesisInfo.genesisChecksum
      ? [ { name: 'genesis_checksum', type: 'string' } ]
      : []),
    ...(genesisInfo.bech32Prefix ? [ { name: 'bech32_prefix', type: 'string' } ] : []),
    ...(genesisInfo.nativeDenom
      ? [ { name: 'native_denom', type: 'TypeGenesisInfoNativeDenom' } ]
      : []),
    { name: 'initial_supply', type: 'string' },
    ...(genesisInfo.sealed ? [ { name: 'sealed', type: 'bool' } ] : []),
    ...(genesisInfo.genesisAccounts ? [ { name: 'genesis_accounts', type: 'TypeGenesisInfoGenesisAccounts' } ] : []),
  ],
  ...(genesisInfo.nativeDenom && {
    TypeGenesisInfoNativeDenom: [
      { name: 'display', type: 'string' },
      { name: 'base', type: 'string' },
      { name: 'exponent', type: 'uint32' },
    ],
  }),
  ...(genesisInfo.genesisAccounts && {
    TypeGenesisInfoGenesisAccounts: [
      { name: 'accounts', type: 'TypeGenesisInfoGenesisAccountsAccounts[]' },
    ],
    TypeGenesisInfoGenesisAccountsAccounts: [
      { name: 'amount', type: 'string' },
      { name: 'address', type: 'string' },
    ],
  }),
};

export const createRollappMetadataProtoMessage = (metadata?: RollappMetadata): { metadata: PartialMessage<RollappMetadataProto> } | undefined => {
  return !metadata ? undefined : {
    metadata: {
      website: metadata.website,
      description: metadata.description,
      logoUrl: metadata.logoUrl,
      telegram: metadata.telegram,
      x: metadata.x,
      genesisUrl: metadata.genesisUrl,
      displayName: metadata.displayName,
      tagline: metadata.tagline,
      explorerUrl: metadata.explorerUrl,
      feeDenom: !metadata.feeDenom ? undefined : {
        display: metadata.feeDenom.display,
        base: metadata.feeDenom.base,
        exponent: metadata.feeDenom.exponent,
      },
      tags: metadata.tags,
    },
  };
};

export const createGenesisInfoProtoMessage = (genesisInfo?: GenesisInfo): { genesisInfo: PartialMessage<GenesisInfoProto> } | undefined => {
  return !genesisInfo ? undefined : {
    genesisInfo: {
      genesisChecksum: genesisInfo.genesisChecksum,
      bech32Prefix: genesisInfo.bech32Prefix,
      nativeDenom: !genesisInfo.nativeDenom ? undefined : {
        display: genesisInfo.nativeDenom.display,
        base: genesisInfo.nativeDenom.base,
        exponent: genesisInfo.nativeDenom.exponent,
      },
      initialSupply: genesisInfo.initialSupply,
      sealed: genesisInfo.sealed,
      genesisAccounts: genesisInfo.genesisAccounts,
    },
  };
};

export const createMetadataEip712Message = (metadata?: RollappMetadata): object | undefined => {
  return !metadata ? undefined : {
    metadata: {
      ...(metadata.website && { website: metadata.website }),
      ...(metadata.description && { description: metadata.description }),
      ...(metadata.logoUrl && { logo_url: metadata.logoUrl }),
      ...(metadata.telegram && { telegram: metadata.telegram }),
      ...(metadata.x && { x: metadata.x }),
      ...(metadata.genesisUrl && { genesis_url: metadata.genesisUrl }),
      ...(metadata.displayName && { display_name: metadata.displayName }),
      ...(metadata.tagline && { tagline: metadata.tagline }),
      ...(metadata.explorerUrl && { explorer_url: metadata.explorerUrl }),
      ...(metadata.feeDenom && {
        fee_denom: {
          display: metadata.feeDenom.display,
          base: metadata.feeDenom.base,
          exponent: metadata.feeDenom.exponent,
        },
      }),
      ...(metadata.tags && { tags: metadata.tags }),
    },
  };
};

export const createGenesisInfoEip712Message = (genesisInfo?: GenesisInfo): object | undefined => {
  return !genesisInfo ? undefined : {
    genesis_info: {
      ...(genesisInfo.genesisChecksum && { genesis_checksum: genesisInfo.genesisChecksum }),
      ...(genesisInfo.bech32Prefix && { bech32_prefix: genesisInfo.bech32Prefix }),
      ...(genesisInfo.nativeDenom && {
        native_denom: {
          display: genesisInfo.nativeDenom.display,
          base: genesisInfo.nativeDenom.base,
          exponent: genesisInfo.nativeDenom.exponent,
        },
      }),
      initial_supply: genesisInfo.initialSupply,
      ...(genesisInfo.sealed && { sealed: genesisInfo.sealed }),
      ...(genesisInfo.genesisAccounts && { genesis_accounts: genesisInfo.genesisAccounts }),
    },
  };
};
